import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import ShopService from '../../../services/ShopService';
import './UserManagement.css';

const UserManagement = () => {
  const { accessToken } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, [pagination.page]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await ShopService.getUsers(pagination.page, pagination.limit);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await ShopService.getRoles();
      setRoles(response.data.roles);
    } catch (err) {
      console.error('Erreur lors du chargement des rôles:', err);
    }
  };

  const handleAssignRole = async (userId, roleId, context = null) => {
    try {
      await ShopService.assignRole(userId, roleId, context);
      setShowRoleModal(false);
      setSelectedUser(null);
      loadUsers(); // Recharger la liste
    } catch (err) {
      setError("Erreur lors de l'assignation du rôle: " + err.message);
    }
  };

  const handleRemoveRole = async (userId, roleId, context = null) => {
    try {
      await ShopService.removeRole(userId, roleId, context);
      loadUsers(); // Recharger la liste
    } catch (err) {
      setError('Erreur lors de la suppression du rôle: ' + err.message);
    }
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setShowRoleModal(true);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'status-active',
      inactive: 'status-inactive',
      pending: 'status-pending',
      locked: 'status-locked',
    };
    return <span className={`status-badge ${statusClasses[status] || 'status-unknown'}`}>{status}</span>;
  };

  if (loading) {
    return <div className="loading-container">Chargement des utilisateurs...</div>;
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h2>Gestion des Utilisateurs</h2>
        <div className="user-stats">
          <span>Total: {pagination.total} utilisateurs</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Email</th>
              <th>Statut</th>
              <th>Rôles</th>
              <th>Dernière connexion</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td>{user.email}</td>
                <td>{getStatusBadge(user.status)}</td>
                <td>
                  <div className="user-roles">
                    {user.roles && user.roles.length > 0 ? (
                      user.roles.map((role, index) => (
                        <span key={index} className="role-badge">
                          {role.name}
                          <button
                            className="remove-role-btn"
                            onClick={() => handleRemoveRole(user.id, role.id, role.context)}
                            title="Supprimer ce rôle"
                          >
                            ×
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="no-roles">Aucun rôle</span>
                    )}
                  </div>
                </td>
                <td>{user.last_login ? new Date(user.last_login).toLocaleDateString('fr-FR') : 'Jamais'}</td>
                <td>
                  <button className="assign-role-btn" onClick={() => openRoleModal(user)}>
                    Gérer les rôles
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            disabled={pagination.page <= 1}
            onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
          >
            Précédent
          </button>
          <span>
            Page {pagination.page} sur {pagination.pages}
          </span>
          <button
            disabled={pagination.page >= pagination.pages}
            onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
          >
            Suivant
          </button>
        </div>
      )}

      {/* Modal d'assignation de rôles */}
      {showRoleModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Assigner des rôles à {selectedUser.email}</h3>
              <button className="close-btn" onClick={() => setShowRoleModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="available-roles">
                <h4>Rôles disponibles</h4>
                {roles.map((role) => (
                  <div key={role.id} className="role-item">
                    <span className="role-name">{role.name}</span>
                    <span className="role-description">{role.description}</span>
                    <button className="assign-btn" onClick={() => handleAssignRole(selectedUser.id, role.id)}>
                      Assigner
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
